import React, { useState, useCallback } from "react";
import { ChevronDown } from "react-feather";
import "./review-line-item.scss";

export interface ReviewLineItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  code?: string;
  name: string;
  subtitle?: string;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  collapsible?: boolean;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  hideBorderTop?: boolean;
  hideBorderRight?: boolean;
  hideBorderBottom?: boolean;
  hideBorderLeft?: boolean;
  children?: React.ReactNode;
}

const ReviewLineItem = ({
  code,
  name,
  subtitle,
  badge,
  actions,
  collapsible = false,
  expanded,
  defaultExpanded = false,
  onExpandChange,
  hideBorderTop = false,
  hideBorderRight = false,
  hideBorderBottom = false,
  hideBorderLeft = false,
  children,
  className,
  style,
  id,
  ...rest
}: ReviewLineItemProps) => {
  const isControlled = expanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expanded : internalExpanded;

  const toggleExpand = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandChange?.(next);
  }, [isExpanded, isControlled, onExpandChange]);

  const cls = "review-line-item";

  const rootClass = [
    cls,
    hideBorderTop ? `${cls}--no-border-top` : "",
    hideBorderRight ? `${cls}--no-border-right` : "",
    hideBorderBottom ? `${cls}--no-border-bottom` : "",
    hideBorderLeft ? `${cls}--no-border-left` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const titleId = id ? `${id}-title` : undefined;

  return (
    <div id={id} className={rootClass} style={style} {...rest}>
      <div className={`${cls}__row`}>
        <div className={`${cls}__info`}>
          {collapsible && (
            <button
              type="button"
              className={[
                `${cls}__chevron`,
                isExpanded ? `${cls}__chevron--expanded` : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={toggleExpand}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              <ChevronDown size={13} />
            </button>
          )}
          {code != null && (
            <span className={`${cls}__code`}>{code}</span>
          )}
          <span id={titleId} className={`${cls}__name`}>
            {name}
          </span>
          {subtitle != null && (
            <span className={`${cls}__subtitle`}>{subtitle}</span>
          )}
          {badge != null && (
            <span className={`${cls}__badge`}>{badge}</span>
          )}
        </div>
        {actions != null && (
          <div className={`${cls}__actions`}>{actions}</div>
        )}
      </div>
      {collapsible && children != null && (
        <div
          className={[
            `${cls}__collapsible`,
            isExpanded
              ? `${cls}__collapsible--expanded`
              : `${cls}__collapsible--collapsed`,
          ].join(" ")}
          aria-hidden={!isExpanded}
        >
          <div className={`${cls}__collapsible-inner`}>
            <div className={`${cls}__content ${cls}__content--indented`}>{children}</div>
          </div>
        </div>
      )}
      {!collapsible && children != null && (
        <div className={`${cls}__content`}>{children}</div>
      )}
    </div>
  );
};

export { ReviewLineItem };
export default ReviewLineItem;
